// src/components/CustomerFlow/CustomerFlow.js

import React, { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck } from '@fortawesome/free-solid-svg-icons';
// Importar estilos base de PurchaseFlow
import '../PurchaseFlow/PurchaseFlow.css';
// Importar estilos específicos de CustomerFlow
import './CustomerFlow.css';

// Importar todos los componentes de pasos
import IdentityValidationStep from './Steps/IdentityValidationStep';
import PaymentProcessingStep from './Steps/PaymentProcessingStep';
import ConfirmationStep from './Steps/ConfirmationStep';
import DocumentUploadStep from './Steps/DocumentUploadStep';
import FinalStep from './Steps/FinalStep';
import { trackMobileCustomerFlowStepEntered, trackMobileCustomerFlowStepCompleted, trackMobileCustomerFlowCompleted } from '../../analytics/events';


const CustomerFlow = ({ 
  flowData, 
  stepStatuses, 
  timeRemaining, 
  onStepComplete,
  onStepBack,
  searchParams
}) => {
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [steps, setSteps] = useState([]);
  const [stepLabels, setStepLabels] = useState([]);
  const [formData, setFormData] = useState({});
  const trackedStepsRef = useRef(new Set()); // Track which steps have been tracked for "entered" event

  // Mapeo de pasos a componentes
  const stepComponentMap = {
    'ValidaStep': IdentityValidationStep,
    'PaymentStep': PaymentProcessingStep,
    'ConfirmationStep': ConfirmationStep,
    'DocumentUploadStep': DocumentUploadStep
  };

  // Mapeo de pasos a etiquetas legibles
  const stepLabelMap = {
    'ValidaStep': 'Identidad',
    'PaymentStep': 'Medio de Pago',
    'ConfirmationStep': 'Confirmación',
    'DocumentUploadStep': 'Documentación'
  };

  useEffect(() => {
    if (flowData?.flow_config && Object.keys(stepStatuses).length > 0) {
      const stepsArray = flowData.flow_config.map(stepName => stepComponentMap[stepName]).filter(Boolean);
      const labelsArray = flowData.flow_config.map(stepName => stepLabelMap[stepName] || stepName);
      
      // El FinalStep siempre está disponible para todos los flujos (pero no se muestra en el stepper)
      stepsArray.push(FinalStep);
      
      setSteps(stepsArray);
      setStepLabels(labelsArray); // Solo las etiquetas de los pasos del flujo
      
      // Determinar el paso actual basado en prioridades
      const targetStepIndex = determineCurrentStepIndex();
      setCurrentStepIndex(targetStepIndex);
      
      // Track step entered when flow initializes (only once per step)
      if (targetStepIndex < stepsArray.length && flowData?.flow_id) {
        const stepName = flowData.flow_config[targetStepIndex];
        if (stepName) {
          const stepKey = `${flowData.flow_id}-${stepName}`;
          if (!trackedStepsRef.current.has(stepKey)) {
            const branchId = localStorage.getItem('temp_tokenBranchId');
            trackMobileCustomerFlowStepEntered({
              commerceId: branchId || 'mobile_flow', // Fallback, should be replaced with actual commerceId from API
              branchId: branchId || undefined,
              stepName: stepName,
              flowId: flowData.flow_id
            });
            trackedStepsRef.current.add(stepKey);
          }
        }
      }
    }
  }, [flowData, stepStatuses]);

  /**
   * Determina el índice del paso actual basado en prioridades
   * Prioridad: in_progress > pending > siguiente después del último procesado > FinalStep
   */
  const determineCurrentStepIndex = () => {
    if (!flowData?.flow_config || Object.keys(stepStatuses).length === 0) {
      return 0;
    }

    const stepNames = flowData.flow_config;
    
    // 1. Buscar paso en progreso (prioridad máxima)
    const inProgressIndex = stepNames.findIndex(
      stepName => stepStatuses[stepName] === 'in_progress'
    );
    if (inProgressIndex !== -1) {
      return inProgressIndex;
    }
    
    // 2. Si todos los pasos están completados, mostrar el paso final (ALTA PRIORIDAD)
    // Nota: 'processed' se considera como un estado válido de completado
    const allStepsCompleted = stepNames.every(stepName => 
      stepStatuses[stepName] === 'completed' || stepStatuses[stepName] === 'processed'
    );
    
    if (allStepsCompleted) {
      // El FinalStep está en el último índice (después de todos los pasos del flujo)
      return stepNames.length; // El paso final está en el último índice
    }
    
    // 3. Buscar primer paso pending
    const pendingIndex = stepNames.findIndex(
      stepName => stepStatuses[stepName] === 'pending'
    );
    if (pendingIndex !== -1) {
      return pendingIndex;
    }
    
    // 4. Buscar siguiente paso después del último procesado/completado
    // Nota: 'processed' se trata como un estado intermedio que permite avanzar
    const lastProcessedIndex = stepNames.findIndex(stepName => 
      stepStatuses[stepName] === 'processed' || stepStatuses[stepName] === 'completed'
    );
    
    if (lastProcessedIndex >= 0 && lastProcessedIndex < stepNames.length - 1) {
      return lastProcessedIndex + 1;
    }
    
    // 5. Fallback: primer paso
    return 0;
  };

  const handleNext = (data, stepStatus = null) => {
    setFormData((prevData) => {
      const updatedData = {
        ...prevData,
        ...data,
      };
      return updatedData;
    });

    // Todos los estados avanzan al siguiente paso
    if (stepStatus === 'completed' || stepStatus === 'processed' || stepStatus === 'in_progress') {
      // Track step completed only when actually completed (not just in_progress)
      // Use a ref to track which steps have been completed to avoid duplicates
      if (stepStatus === 'completed' || stepStatus === 'processed') {
        const stepName = flowData?.flow_config?.[currentStepIndex];
        if (stepName && flowData?.flow_id) {
          const stepCompletedKey = `${flowData.flow_id}-${stepName}-completed`;
          if (!trackedStepsRef.current.has(stepCompletedKey)) {
            const branchId = localStorage.getItem('temp_tokenBranchId');
            trackMobileCustomerFlowStepCompleted({
              commerceId: branchId || 'mobile_flow', // Fallback, should be replaced with actual commerceId
              branchId: branchId || undefined,
              stepName: stepName,
              flowId: flowData.flow_id
            });
            trackedStepsRef.current.add(stepCompletedKey);
          }
        }
      }
      
      // Cualquier estado del paso - avanzar al siguiente
      onStepComplete(data);
      
      // Recalcular el paso actual después de completar
      setTimeout(() => {
        const newStepIndex = determineCurrentStepIndex();
        setCurrentStepIndex(newStepIndex);
        
        // Track step entered (only once per step)
        const newStepName = flowData?.flow_config?.[newStepIndex];
        if (newStepName && flowData?.flow_id) {
          const stepKey = `${flowData.flow_id}-${newStepName}`;
          if (!trackedStepsRef.current.has(stepKey)) {
            const branchId = localStorage.getItem('temp_tokenBranchId');
            trackMobileCustomerFlowStepEntered({
              commerceId: branchId || 'mobile_flow', // Fallback
              branchId: branchId || undefined,
              stepName: newStepName,
              flowId: flowData.flow_id
            });
            trackedStepsRef.current.add(stepKey);
          }
        }
        
        // Track flow completed if we're on FinalStep
        if (newStepIndex === steps.length - 1 && flowData?.flow_id) {
          const branchId = localStorage.getItem('temp_tokenBranchId');
          trackMobileCustomerFlowCompleted({
            commerceId: branchId || 'mobile_flow', // Fallback
            branchId: branchId || undefined,
            flowId: flowData.flow_id
          });
        }
      }, 100);
    }
  };

  const handleBack = () => {
    if (currentStepIndex > 0) {
      setCurrentStepIndex(prev => prev - 1);
      onStepBack();
    }
  };

  const renderStep = () => {
    const CurrentStepComponent = steps[currentStepIndex];

    if (!CurrentStepComponent) {
      return <div>Error al cargar el paso. Por favor, intenta nuevamente.</div>;
    }

    // Props específicas para el paso final
    if (CurrentStepComponent === FinalStep) {
      return (
        <FinalStep
          onComplete={handleNext}
        />
      );
    }

    return (
      <CurrentStepComponent
        flowData={flowData}
        stepStatuses={stepStatuses}
        timeRemaining={timeRemaining}
        onNext={handleNext}
        onBack={handleBack}
        formData={formData}
        currentStepIndex={currentStepIndex}
        totalSteps={steps.length}
        searchParams={searchParams}
      />
    );
  };

  // Función para calcular el progreso
  const calculateProgress = () => {
    const totalSteps = steps.length;
    if (totalSteps === 0) return 0;

    // El progreso se calcula solo sobre los pasos del flujo configurado
    const flowSteps = flowData?.flow_config?.length || 0;
    if (flowSteps === 0) return 0;

    // Si estamos en el FinalStep, mostrar 100%
    if (currentStepIndex === flowSteps) {
      return 100;
    }

    // Contar pasos completados (tanto 'completed' como 'processed')
    const completedSteps = Object.values(stepStatuses).filter(status => 
      status === 'completed' || status === 'processed'
    ).length;
    
    // El progreso se calcula solo sobre los pasos del flujo
    return (completedSteps / flowSteps) * 100;
  };

  if (!steps.length) {
    return (
      <div className="text-center mt-5">
        <div className="spinner-border text-primary mb-3" role="status">
          <span className="visually-hidden">Cargando...</span>
        </div>
        <p className="text-muted">Configurando tu proceso...</p>
      </div>
    );
  }

  return (
    <div className="customer-flow">
      {/* Stepper con barra de progreso - solo visible si hay más de 1 paso */}
      {stepLabels.length > 1 && (
        <div className="stepper-container mb-4">
          <div className="stepper-wrapper">
            {stepLabels.map((label, index) => {
              const stepName = flowData?.flow_config?.[index];
              const isCurrentStep = index === currentStepIndex;
              
              let stepClassName = 'stepper-item';
              if (isCurrentStep) {
                stepClassName += ' active';
              } else if (stepStatuses[stepName] === 'completed' || stepStatuses[stepName] === 'processed') {
                stepClassName += ' completed';
              }

              return (
                <div className={stepClassName} key={index}>
                  <div className="step-counter">
                    {stepStatuses[stepName] === 'completed' || stepStatuses[stepName] === 'processed' ? (
                      <FontAwesomeIcon icon={faCheck} className="text-white" />
                    ) : (
                      index + 1
                    )}
                  </div>
                  <p className="step-label">{label}</p>
                </div>
              );
            })}
          </div>
          
          {/* Barra de progreso */}
          <div className="progress-bar-wrapper">
            <div
              className={`progress-bar ${calculateProgress() === 100 ? 'success' : ''}`}
              style={{
                width: `${calculateProgress()}%`,
                transition: 'width 0.3s ease-in-out'
              }}
            ></div>
          </div>
        </div>
      )}

      {/* Renderizar el componente del paso actual */}
      {renderStep()}
    </div>
  );
};

export default CustomerFlow;
